import { useState } from 'react';
import { useSettings } from '../context/SettingsContext.jsx';
import { uploadUrl } from '../api/client.js';

// Port persis dari includes/contact.php — info kontak + copy-to-clipboard,
// tombol sosial media, dan embed Google Maps. Dipakai di section bawah homepage.
export default function ContactInfoSection() {
  const { settings } = useSettings();
  const [copied, setCopied] = useState(null);

  const isOpenNow = (() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun..6=Sat
    const hour = now.getHours();
    return day >= 1 && day <= 5 && hour >= 8 && hour < 17;
  })();

  const copy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const waLink = settings.whatsapp
    ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent('Halo, saya ingin bertanya tentang layanan event hewan Anda.')}`
    : null;

  const socials = [
    { key: 'instagram', href: settings.instagram, cls: 'social-ig', title: 'Instagram', icon: <IgIcon /> },
    { key: 'facebook', href: settings.facebook, cls: 'social-fb', title: 'Facebook', icon: <FbIcon /> },
    { key: 'youtube', href: settings.youtube, cls: 'social-yt', title: 'YouTube', icon: <YtIcon /> },
    { key: 'tiktok', href: settings.tiktok, cls: 'social-tt', title: 'TikTok', icon: <TtIcon /> },
    { key: 'whatsapp', href: waLink, cls: 'social-wa wa-pulse', title: 'WhatsApp', icon: <WaIcon /> },
  ].filter((s) => s.href);

  const mapsEmbedUrl = settings.maps_embed_url || '';
  const mapsViewUrl = settings.maps_view_url || '';
  const mapsLabel = settings.maps_label || settings.site_name || '';

  return (
    <section id="kontak" className="py-20 md:py-28 dark:bg-neutral-900 transition-colors duration-300" style={{ backgroundImage: 'linear-gradient(180deg, transparent, color-mix(in oklab, var(--accent) 4%, transparent), transparent)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4 border" style={{ background: 'color-mix(in oklab, var(--accent) 8%, transparent)', borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
            <span className="material-symbols-outlined text-base">mail</span> HUBUNGI KAMI
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 tracking-tight">
            Kami Siap <span style={{ color: 'var(--accent)' }}>Membantu</span> Anda
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 max-w-xl mx-auto text-base md:text-lg">
            Hubungi tim kami untuk informasi lebih lanjut tentang layanan event hewan kami.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Kontak Utama */}
          <div className="rounded-3xl p-8 text-white flex flex-col justify-between shadow-lg" style={{ background: 'linear-gradient(135deg, var(--accent), var(--color-forest-700))' }}>
            <div>
              <h3 className="font-display text-xl font-bold mb-6 tracking-wide uppercase text-white/90">Informasi Kontak</h3>
              <div className="space-y-6">
                <InfoRow icon="location_on" label="Alamat Kantor" value={settings.alamat} />

                <div className="flex items-start gap-4">
                  <IconBox icon="call" />
                  <div>
                    <div className="text-xs text-white/60 font-semibold uppercase tracking-wider mb-1">Telepon / Hotline</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white/90 font-medium">{settings.telepon}</span>
                      {settings.telepon && (
                        <button onClick={() => copy(settings.telepon, 'telepon')} className="inline-flex items-center opacity-60 hover:opacity-100 transition-opacity" title="Salin nomor">
                          <span className="material-symbols-outlined text-base">content_copy</span>
                        </button>
                      )}
                      {copied === 'telepon' && <span className="text-[11px] bg-white/20 text-white px-2 py-0.5 rounded-full">Tersalin!</span>}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <IconBox icon="mail" />
                  <div>
                    <div className="text-xs text-white/60 font-semibold uppercase tracking-wider mb-1">Email</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white/90 font-medium">{settings.email}</span>
                      {settings.email && (
                        <button onClick={() => copy(settings.email, 'email')} className="inline-flex items-center opacity-60 hover:opacity-100 transition-opacity" title="Salin email">
                          <span className="material-symbols-outlined text-base">content_copy</span>
                        </button>
                      )}
                      {copied === 'email' && <span className="text-[11px] bg-white/20 text-white px-2 py-0.5 rounded-full">Tersalin!</span>}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <IconBox icon="schedule" />
                  <div>
                    <div className="text-xs text-white/60 font-semibold uppercase tracking-wider mb-1">Jam Operasional</div>
                    <div className="text-sm text-white/90 font-medium">{settings.jam_operasional}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: isOpenNow ? '#4ade80' : '#f87171', boxShadow: isOpenNow ? '0 0 0 2px rgba(74,222,128,0.3)' : 'none' }} />
                      <span className="text-xs font-semibold" style={{ color: isOpenNow ? '#4ade80' : '#f87171' }}>{isOpenNow ? 'Buka Sekarang' : 'Sedang Tutup'}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-white/50">
                      <span className="material-symbols-outlined text-sm" style={{ color: '#86efac' }}>bolt</span>
                      <span className="text-xs">Biasanya membalas dalam <strong className="text-white/70">1 jam</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3 justify-start">
              {settings.telepon && (
                <a href={`tel:${settings.telepon}`} title="Telepon"
                  className="w-12 h-12 flex items-center justify-center rounded-3xl bg-white/20 hover:rounded-xl active:rounded-lg active:scale-[0.98] text-white transition-all duration-300 ease-in-out border border-white/20 shadow-md hover:!bg-[#fbc024] hover:!text-black active:!bg-[#fbc024] active:!text-black">
                  <span className="material-symbols-outlined text-2xl">phone</span>
                </a>
              )}
              {settings.email && (
                <a href={`mailto:${settings.email}`} title="Email"
                  className="w-12 h-12 flex items-center justify-center rounded-3xl bg-white/20 hover:rounded-xl active:rounded-lg active:scale-[0.98] text-white transition-all duration-300 ease-in-out border border-white/20 shadow-md hover:!bg-[#fbc024] hover:!text-black active:!bg-[#fbc024] active:!text-black">
                  <span className="material-symbols-outlined text-2xl">mail</span>
                </a>
              )}
            </div>
          </div>

          {/* Sosial Media & Map */}
          <div className="flex flex-col gap-6">
            {socials.length > 0 && (
              <div className="rounded-3xl p-6 border shadow-sm bg-white dark:bg-neutral-800 transition-all" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400 mb-4">Ikuti Kami</h4>
                <div className="flex gap-3 flex-wrap">
                  {socials.map((s) => (
                    <a key={s.key} href={s.href} target="_blank" rel="noreferrer" title={s.title}
                      className={`social-btn ${s.cls} inline-flex items-center justify-center w-11 h-11 rounded-xl text-white shadow-md hover:scale-110 hover:rounded-2xl active:rounded-3xl active:scale-95 transition-all duration-300`}>
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {mapsEmbedUrl && (
              <div className="rounded-3xl overflow-hidden border flex-1 shadow-sm" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
                {mapsLabel && (
                  <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-neutral-800 border-b" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-base" style={{ color: '#e53935' }}>location_on</span>
                      <span className="text-sm font-bold text-neutral-800 dark:text-white">{mapsLabel}</span>
                    </div>
                    {mapsViewUrl && (
                      <a href={mapsViewUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105 hover:rounded-xl active:rounded-2xl active:scale-95" style={{ background: 'color-mix(in oklab, var(--accent) 10%, transparent)', color: 'var(--accent)' }}>
                        <span className="material-symbols-outlined text-sm">open_in_new</span> Buka di Maps
                      </a>
                    )}
                  </div>
                )}
                <iframe src={mapsEmbedUrl} width="100%" height="280" style={{ border: 0, display: 'block' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Lokasi" />
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .social-ig { background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%); }
        .social-fb { background: #1877F2; }
        .social-yt { background: #FF0000; }
        .social-tt { background: #000000; }
        .social-wa { background: #25D366; }
      `}</style>
    </section>
  );
}

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-4">
      <IconBox icon={icon} />
      <div>
        <div className="text-xs text-white/60 font-semibold uppercase tracking-wider mb-1">{label}</div>
        <div className="text-sm leading-relaxed text-white/90">{value}</div>
      </div>
    </div>
  );
}

function IconBox({ icon }) {
  return (
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-inner" style={{ background: 'rgba(255,255,255,0.12)' }}>
      <span className="material-symbols-outlined text-lg" style={{ color: '#fcd34d' }}>{icon}</span>
    </div>
  );
}

function IgIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>;
}
function FbIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z"/></svg>;
}
function YtIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
}
function TtIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>;
}
function WaIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>;
}